import { Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { isReviewAccessEnabled, shouldAllowAccess } from '../../utils/environment';
import { LandingPage } from '../../pages/LandingPage';
import { LoginPage } from '../../pages/LoginPage';
import { SignUpPage } from '../../pages/SignUpPage';
import { EmailLinkSignInPage } from '../../pages/EmailLinkSignInPage';
import { MainLayout } from '../layout/MainLayout';
import { AuthGuard } from '../auth/AuthGuard';
import { lazy } from 'react';
import { Loader } from '../shared/Loader';

// Lazy load pages to reduce main bundle size
const HomePage = lazy(() => import('../../pages/HomePage').then(m => ({ default: m.HomePage })));
const PlayPage = lazy(() => import('../../pages/PlayPage'));
const MePage = lazy(() => import('../../pages/MePage').then(m => ({ default: m.MePage })));
const ThemePage = lazy(() => import('../../pages/ThemePage').then(m => ({ default: m.ThemePage })));
const PlansPage = lazy(() => import('../../pages/PlansPage').then(m => ({ default: m.PlansPage })));
const PlanDetailPage = lazy(() => import('../../pages/PlanDetailPage').then(m => ({ default: m.PlanDetailPage })));
const RememberPage = lazy(() => import('../../pages/RememberPage').then(m => ({ default: m.RememberPage })));
const SignalsPage = lazy(() => import('../../pages/SignalsPage').then(m => ({ default: m.SignalsPage })));

export function AppRoutes() {
  const { user, initialized } = useAuthStore();
  const allowAccess = shouldAllowAccess();
  const reviewAccess = isReviewAccessEnabled();

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {/* Landing page route - redirect authenticated users to home */}
        <Route 
          path="/" 
          element={
            user ? <Navigate to="/today" replace /> : <LandingPage />
          } 
        />
        
        {/* Auth routes - Always allow access to login/signup pages */}
        <Route path="/login" element={
          user ? <Navigate to="/today" replace /> : <LoginPage />
        } />
        
        <Route path="/login/email-link" element={
          user ? <Navigate to="/today" replace /> : <EmailLinkSignInPage />
        } />
        
        <Route path="/signup" element={
          user ? <Navigate to="/today" replace /> : reviewAccess ? <SignUpPage /> : <Navigate to="/" replace />
        } />
        
        {/* Protected routes */}
        <Route element={
          <AuthGuard allowAppAccess={allowAccess}>
            <MainLayout>
              <Outlet />
            </MainLayout>
          </AuthGuard>
        }>
          <Route path="/today" element={<HomePage />} />
          <Route path="/signals" element={<SignalsPage />} />
          <Route path="/me" element={<MePage />} />
          <Route path="/theme" element={<ThemePage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/plans/:planId" element={<PlanDetailPage />} />
          <Route path="/remember" element={<RememberPage />} />
          <Route path="/play" element={<PlayPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
} 
